import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { AssociationPastRequest } from '@cobliteam/api-lib/src/dashboard/vehicle/Types';
import { useAssignDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { buildImageAssignRequests } from '@cobliteam/hooks/src/useCamera/useFaceId';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import Alert from '@cobliteam/ui/components/Alert';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import DataTableSelectActions from '@cobliteam/ui-business/components/DataTable/components/DataTableSelectActions';
import { RawIdentificationSource } from '@cobliteam/ui-business/components/MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';
import SelectDriver from '@cobliteam/ui-business/components/Selects/SelectDriver';
import type { DriverOption } from '@cobliteam/ui-business/components/Selects/SelectDriver/SelectDriver';
import { formatISO } from '@cobliteam/utils/date';
import { useQueryClient } from '@tanstack/react-query';
import type { Table as ITable } from '@tanstack/react-table';
import { useState } from 'react';
import ConfirmationDialog from './components/ConfirmationDialog';

interface PathSelectActionProps {
  columnsDef: any;
  table: ITable<Path>;
  onApply: () => void;
}

const PathSelectAction = ({ columnsDef, table, onApply }: PathSelectActionProps) => {
  const [driver, setDriver] = useState<DriverOption | undefined>(undefined);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { createDriverAssociationRequestBatch } = useFuscaVehicle();
  const { mutateAsync } = createDriverAssociationRequestBatch;
  const { mutateAsync: assignFaceImages } = useAssignDriverFaceImage();

  const handleSelection = (newDriver?: DriverOption) => {
    setDriver(newDriver);
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    const selectedItems = table.getFilteredSelectedRowModel().rows;
    if (driver && selectedItems.length) {
      const associationsRequests: AssociationPastRequest[] = selectedItems.map((pathRow) => ({
        driverId: driver.id,
        startTime: formatISO(pathRow.original.startTime + 1000), //+ 1 second is used to ensure reference time is after path start
        source: RawIdentificationSource.PATH_REVIEW.valueOf(),
      }));
      try {
        const vehicleId = selectedItems[0].original.vehicle?.id!;
        await mutateAsync({ vehicleId, associationsRequests });
        table.toggleAllPageRowsSelected(false);
        Alert.success('Motorista associado com sucesso!');

        const imageAssignments = buildImageAssignRequests(
          queryClient,
          selectedItems.map((row) => row.original),
          driver.id
        );

        if (imageAssignments.length > 0) {
          assignFaceImages(imageAssignments).catch(() => {});
        }
      } catch (_ex) {
        Alert.error('Erro ao associar motorista!');
      } finally {
        setOpenDialog(false);
        onApply();
      }
    }
    setOpenDialog(false);
  };

  return (
    <DataTableSelectActions table={table} columnsDef={columnsDef} className='bg-white'>
      <div className='flex items-center gap-3'>
        <Typography variant='description-title' color='secondary'>
          {table.getFilteredSelectedRowModel().rows.length === 1 ? '1 selecionado' : `${table.getFilteredSelectedRowModel().rows.length} selecionados`}
        </Typography>
        <Button size='sm' variant='outlined' color='default' endIcon={<Icon name='list-checks' />} onClick={() => table.toggleAllPageRowsSelected(true)}>
          Selecionar todos
        </Button>

        <Button size='sm' variant='outlined' color='default' onClick={() => table.toggleAllPageRowsSelected(false)}>
          Limpar seleção
        </Button>

        <SelectDriver value={driver} onChange={handleSelection} />
      </div>

      <ConfirmationDialog open={openDialog} onOpenChange={setOpenDialog} onConfirm={handleConfirm} />
    </DataTableSelectActions>
  );
};

export default PathSelectAction;
